import type { TFunction } from 'i18next';
import type { FilterFieldOption } from '~/components/ui';
import { RequestForQuoteArchivedFilter } from '~/graphql/graphql';
import {
  HAS_QUOTE_VALUE,
  REQUEST_FOR_QUOTE_FILTER_FIELD,
  isCollectionField,
  newRequestForQuoteFilterRowId,
  type RequestForQuoteFilterRow,
} from './requestForQuoteFilters';

type RequestForQuoteTranslate = TFunction<'my-requests-for-quote'>;

export function initialValueFor(field: string): string | string[] {
  if (isCollectionField(field)) return [];
  if (
    field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasAcceptedQuote ||
    field === REQUEST_FOR_QUOTE_FILTER_FIELD.hasArchivedQuote
  ) {
    return HAS_QUOTE_VALUE.yes;
  }
  if (field === REQUEST_FOR_QUOTE_FILTER_FIELD.archived) {
    return RequestForQuoteArchivedFilter.Include;
  }
  return '';
}

export function newRequestForQuoteFilterRow(): RequestForQuoteFilterRow {
  return {
    id: newRequestForQuoteFilterRowId(),
    field: REQUEST_FOR_QUOTE_FILTER_FIELD.platformProductIds,
    operator: 'in',
    value: [],
    connector: 'and',
  };
}

export function newArchivedIncludeRow(): RequestForQuoteFilterRow {
  return {
    id: newRequestForQuoteFilterRowId(),
    field: REQUEST_FOR_QUOTE_FILTER_FIELD.archived,
    operator: 'eq',
    value: RequestForQuoteArchivedFilter.Include,
    connector: 'and',
  };
}

export function requestForQuoteFilterFields(
  t: RequestForQuoteTranslate,
  isArchivedFilterAvailable: boolean
): FilterFieldOption[] {
  const collectionOperatorOptions = [
    { label: t('my_requests_for_quote.filter.operator_in'), operator: 'in' },
    {
      label: t('my_requests_for_quote.filter.operator_not_in'),
      operator: 'notIn',
    },
  ];
  const equalityOperatorOptions = [
    { label: t('my_requests_for_quote.filter.operator_eq'), operator: 'eq' },
  ];

  const yesNoOptions = [
    {
      label: t('my_requests_for_quote.filter.value_yes'),
      value: HAS_QUOTE_VALUE.yes,
    },
    {
      label: t('my_requests_for_quote.filter.value_no'),
      value: HAS_QUOTE_VALUE.no,
    },
  ];

  const fields: FilterFieldOption[] = [
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.platformProductIds,
      label: t('my_requests_for_quote.filter.field_product'),
      operatorOptions: collectionOperatorOptions,
      inputType: 'select',
    },
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.sellingOrganizationIds,
      label: t('my_requests_for_quote.filter.field_seller'),
      operatorOptions: collectionOperatorOptions,
      inputType: 'select',
    },
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.createdByUserIds,
      label: t('my_requests_for_quote.filter.field_created_by'),
      operatorOptions: collectionOperatorOptions,
      inputType: 'select',
    },
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.hasAcceptedQuote,
      label: t('my_requests_for_quote.filter.field_accepted_quote'),
      operatorOptions: equalityOperatorOptions,
      inputType: 'select',
      selectOptions: yesNoOptions,
    },
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.hasArchivedQuote,
      label: t('my_requests_for_quote.filter.field_archived_quote'),
      operatorOptions: equalityOperatorOptions,
      inputType: 'select',
      selectOptions: yesNoOptions,
    },
    {
      field: REQUEST_FOR_QUOTE_FILTER_FIELD.archived,
      label: t('my_requests_for_quote.filter.field_archived'),
      operatorOptions: equalityOperatorOptions,
      inputType: 'select',
      selectOptions: [
        {
          label: t('my_requests_for_quote.filter.value_include'),
          value: RequestForQuoteArchivedFilter.Include,
        },
        {
          label: t('my_requests_for_quote.filter.value_only'),
          value: RequestForQuoteArchivedFilter.Only,
        },
      ],
    },
  ];
  return isArchivedFilterAvailable
    ? fields
    : fields.filter(
        (option) => option.field !== REQUEST_FOR_QUOTE_FILTER_FIELD.archived
      );
}
